import React, { useEffect, useRef, useState } from "react";
import axiosInstance from "../../../components/api/axiosInstance";
import { getAuthItem } from "../../../utils/authStorage";
import { errorMessage, shortCodeFrom } from "../dmApi";

const NEW_OWNER = "__new__";
const DESCRIPTION_MAX = 500;
const PRICE_MAX = 100;

function currentUserId() {
  try {
    const user = JSON.parse(getAuthItem("user") || "null");
    return user?.user_id ?? user?.id ?? null;
  } catch {
    return null;
  }
}

const listFrom = (res) => (Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : []);

/** Brochure JSON (website property-page format: { meta, property }) → form fields. */
function fieldsFromDetails(details) {
  const p = details?.property || details || {};
  const text = (v) => (v == null ? "" : String(v).trim());
  const amenities = Array.isArray(p.amenities) ? p.amenities.map(text).filter(Boolean) : [];
  return {
    name: text(p.title || p.name),
    type: text(p.type || details?.meta?.propertyType),
    address: text(p.location || p.address),
    description: text(p.overview || p.description).slice(0, DESCRIPTION_MAX),
    price: text(p.price).slice(0, PRICE_MAX),
    amenities: amenities.join("\n"),
    url: text(p.url || details?.meta?.url || details?.meta?.canonical),
  };
}

const EMPTY_FORM = { name: "", code: "", type: "", address: "", price: "", amenities: "", description: "", url: "" };

/**
 * Staff: add a client (tenant) project. Projects belong to a project owner (tenant client);
 * a new owner can be created inline, defaulting to the client's own business name.
 * Details can be auto-filled from the brochure JSON the website property page uses.
 */
function ClientProjectForm({ tenantId, tenantName, onSaved, onCancel }) {
  const [owners, setOwners] = useState([]);
  const [loadingOwners, setLoadingOwners] = useState(true);
  const [ownerId, setOwnerId] = useState("");
  const [ownerName, setOwnerName] = useState(tenantName || "");
  const [form, setForm] = useState(EMPTY_FORM);
  const [codeTouched, setCodeTouched] = useState(false);
  const [details, setDetails] = useState(null);
  const [showPaste, setShowPaste] = useState(false);
  const [pasted, setPasted] = useState("");
  const [brochure, setBrochure] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const jsonInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get("/tenant-clients/", { params: { tenant_id: tenantId } })
      .then((res) => {
        if (cancelled) return;
        const rows = listFrom(res)
          .filter((r) => String(r.Status || "Active").toLowerCase() !== "inactive")
          .map((r) => ({ id: String(r.Tenant_Client_Id), name: r.Client_Name || `Owner #${r.Tenant_Client_Id}` }));
        setOwners(rows);
        setOwnerId(rows.length === 1 ? rows[0].id : rows.length ? "" : NEW_OWNER);
      })
      .catch(() => !cancelled && setOwnerId(NEW_OWNER))
      .finally(() => !cancelled && setLoadingOwners(false));
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const set = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => ({
      ...f,
      [key]: value,
      ...(key === "name" && !codeTouched ? { code: shortCodeFrom(value) } : {}),
    }));
  };

  const applyDetails = (text) => {
    setError("");
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      setError("That is not valid JSON.");
      return false;
    }
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      setError("The JSON must be an object (e.g. { \"meta\": …, \"property\": … }).");
      return false;
    }
    const filled = fieldsFromDetails(parsed);
    setDetails(parsed);
    setForm((f) => {
      const next = { ...f };
      Object.entries(filled).forEach(([k, v]) => {
        if (v) next[k] = v;
      });
      if (filled.name && !codeTouched) next.code = shortCodeFrom(filled.name);
      return next;
    });
    setNotice("Details filled from the brochure JSON. Review them before saving.");
    return true;
  };

  const onJsonFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => applyDetails(String(reader.result || ""));
    reader.onerror = () => setError("Could not read the JSON file.");
    reader.readAsText(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!ownerId) return setError("Select a project owner.");
    if (ownerId === NEW_OWNER && !ownerName.trim()) return setError("Enter the project owner name.");
    if (!form.name.trim() || !form.code.trim()) return setError("Project name and short code are required.");

    const userId = currentUserId();
    setSaving(true);
    try {
      let clientId = ownerId;
      if (ownerId === NEW_OWNER) {
        const res = await axiosInstance.post("/tenant-clients/", {
          Tenant_Id: tenantId,
          Client_Name: ownerName.trim(),
          Created_By: userId,
          Updated_By: userId,
        });
        clientId = res.data?.data?.Tenant_Client_Id;
        if (!clientId) throw new Error("Could not create the project owner.");
        setOwners((list) => [...list, { id: String(clientId), name: ownerName.trim() }]);
        setOwnerId(String(clientId));
      }
      const res = await axiosInstance.post("/tenant-projects/", {
        Tenant_Id: tenantId,
        Tenant_Client_Id: clientId,
        Project_Name: form.name.trim(),
        Short_Code: form.code.trim().toUpperCase(),
        Project_Type: form.type.trim() || null,
        Project_URL: form.url.trim() || null,
        Address: form.address.trim() || null,
        Rent_Range: form.price.trim() || null,
        Amenities: form.amenities.split("\n").map((a) => a.trim()).filter(Boolean),
        Description: form.description.trim() || null,
        Property_Details: details,
        Created_By: userId,
        Updated_By: userId,
      });
      const projectId = res.data?.data?.Tenant_Project_Id;
      if (brochure && projectId) {
        const body = new FormData();
        body.append("file", brochure);
        try {
          await axiosInstance.post(`/tenant-projects/${projectId}/brochure`, body, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (err) {
          onSaved();
          window.alert(`Project saved, but the brochure upload failed: ${err.response?.data?.message || err.response?.data?.error || err.message}`);
          return;
        }
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || errorMessage(err, "Could not save project"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="dmp-card dmc-project-form" onSubmit={submit}>
      <div className="dmc-project-form__head">
        <h3 className="dmp-card__title">New project{tenantName ? ` for ${tenantName}` : ""}</h3>
        <div className="dmc-project-form__import">
          <button type="button" className="dmp-btn dmp-btn--ghost dmp-btn--sm" onClick={() => jsonInputRef.current?.click()}>
            Import details JSON
          </button>
          <button type="button" className="dmp-btn dmp-btn--ghost dmp-btn--sm" onClick={() => setShowPaste((v) => !v)}>
            {showPaste ? "Hide paste" : "Paste JSON"}
          </button>
          <input ref={jsonInputRef} type="file" accept=".json,application/json" hidden onChange={onJsonFile} />
        </div>
      </div>
      {showPaste ? (
        <div className="dmc-project-form__paste">
          <textarea
            rows={5}
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
            placeholder='Paste the brochure JSON, e.g. { "meta": {...}, "property": { "title": ..., "location": ..., "amenities": [...] } }'
          />
          <button
            type="button"
            className="dmp-btn dmp-btn--sm"
            disabled={!pasted.trim()}
            onClick={() => applyDetails(pasted) && setShowPaste(false)}
          >
            Fill details
          </button>
        </div>
      ) : null}
      {error ? <div className="dmp-error">{error}</div> : null}
      {notice && !error ? <div className="dmp-notice">{notice}</div> : null}
      <div className="dmp-form-grid">
        <label className="dmp-field">
          Project Owner *
          <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)} disabled={loadingOwners}>
            {loadingOwners ? <option value="">Loading…</option> : null}
            {!loadingOwners && owners.length > 1 ? <option value="">Select owner</option> : null}
            {owners.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
            {!loadingOwners ? <option value={NEW_OWNER}>+ New owner</option> : null}
          </select>
        </label>
        {ownerId === NEW_OWNER ? (
          <label className="dmp-field">
            Owner Name *
            <input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder="Owner / builder name" />
          </label>
        ) : null}
        <label className="dmp-field">
          Project Name *
          <input value={form.name} onChange={set("name")} placeholder="e.g. Green Valley Villas" autoFocus />
        </label>
        <label className="dmp-field">
          Short Code *
          <input
            value={form.code}
            onChange={(e) => {
              setCodeTouched(true);
              set("code")(e);
            }}
            maxLength={20}
            placeholder="e.g. GVV"
          />
        </label>
        <label className="dmp-field">
          Project Type
          <input value={form.type} onChange={set("type")} placeholder="e.g. Villa Plots, Apartments" maxLength={100} />
        </label>
        <label className="dmp-field">
          Price / Sizes
          <input value={form.price} onChange={set("price")} placeholder="e.g. ₹45L onwards" maxLength={PRICE_MAX} />
        </label>
        <label className="dmp-field dmp-field--full">
          Project Page Link
          <input
            type="url"
            value={form.url}
            onChange={set("url")}
            placeholder="https://builder-site.com/property-details.html?id=..."
            maxLength={1000}
          />
        </label>
        <label className="dmp-field dmp-field--full">
          Address / Location
          <input value={form.address} onChange={set("address")} placeholder="Project location" />
        </label>
        <label className="dmp-field dmp-field--full">
          Description
          <textarea rows={3} value={form.description} onChange={set("description")} maxLength={DESCRIPTION_MAX} />
        </label>
        <label className="dmp-field dmp-field--full">
          Amenities (one per line)
          <textarea rows={3} value={form.amenities} onChange={set("amenities")} />
        </label>
        <label className="dmp-field dmp-field--full">
          Brochure / Flyer
          <input
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp"
            onChange={(e) => setBrochure(e.target.files?.[0] || null)}
          />
        </label>
      </div>
      {details ? (
        <p className="dmp-muted dmc-project-form__saved-json">
          Brochure JSON attached — it will be saved with the project for the website property page.{" "}
          <button type="button" className="dmp-link" onClick={() => setDetails(null)}>
            Remove
          </button>
        </p>
      ) : null}
      <div className="dmc-project-form__actions">
        <button type="button" className="dmp-btn dmp-btn--ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="submit" className="dmp-btn" disabled={saving}>
          {saving ? "Saving…" : "Save project"}
        </button>
      </div>
    </form>
  );
}

export default ClientProjectForm;
